"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectDateDatasetsForInsight = exports.QueryDateDatasetsForInsightService = void 0;
var tslib_1 = require("tslib");
var effects_1 = require("redux-saga/effects");
var compact_1 = tslib_1.__importDefault(require("lodash/compact"));
var fromPairs_1 = tslib_1.__importDefault(require("lodash/fromPairs"));
var uniqBy_1 = tslib_1.__importDefault(require("lodash/uniqBy"));
var ts_invariant_1 = require("ts-invariant");
var sdk_model_1 = require("@gooddata/sdk-model");
var queryService_1 = require("../store/_infra/queryService");
var queries_1 = require("../queries");
var insightsSelectors_1 = require("../store/insights/insightsSelectors");
var general_1 = require("../events/general");
var queryCall_1 = require("../store/_infra/queryCall");
var catalogSelectors_1 = require("../store/catalog/catalogSelectors");
var backendCapabilitiesSelectors_1 = require("../store/backendCapabilities/backendCapabilitiesSelectors");
var objRefMap_1 = require("../../_staging/metadata/objRefMap");
var dateDatasetOrdering_1 = require("../../_staging/catalog/dateDatasetOrdering");
var loadAvailableDateDatasets_1 = require("./loadAvailableDateDatasets");
exports.QueryDateDatasetsForInsightService = queryService_1.createCachedQueryService("GDC.DASH/QUERY.INSIGHT.DATE.DATASETS", queryService, function (query) {
    var insightOrRef = query.payload.insightOrRef;
    var ref = sdk_model_1.isObjRef(insightOrRef) ? insightOrRef : sdk_model_1.insightRef(insightOrRef);
    return sdk_model_1.serializeObjRef(ref);
});
/**
 * Selector that will return date datasets for insight. The input to the selector is the dashboard query that is used
 * to obtain and cache the data.
 *
 * This selector will return undefined if the query to obtain the data for particular insight was not yet fired or
 * processed. Otherwise will return object containing `status` of the data retrieval; if the `status` is
 * `'success'` then the `result` prop will contain the data.
 *
 * @remarks see {@link QueryInsightDateDatasets}
 * @internal
 */
exports.selectDateDatasetsForInsight = exports.QueryDateDatasetsForInsightService.cache.selectQueryResult;
//
// Query implementation
//
/**
 * Given insight and a list of available cataloged date datasets, this function looks up date datasets that are used
 * in insight's global date filters. Date datasets
 *
 * @param insight - insight to lookup date dataset for
 * @param datasets - all cataloged date datasets
 */
function lookupDatasetsUsedInDateFilters(insight, datasets) {
    var dateFilters = sdk_model_1.insightFilters(insight).filter(sdk_model_1.isDateFilter);
    return dateFilters.map(function (filter) {
        var datasetRef = sdk_model_1.filterObjRef(filter);
        var dateDataset = datasets.get(datasetRef);
        // if this happens then either component is using wrong catalog or metadata is inconsistent (and insight thus un-renderable)
        ts_invariant_1.invariant(dateDataset, "cannot find metadata for date dataset " + sdk_model_1.objRefToString(datasetRef));
        return dateDataset;
    });
}
/**
 * Given insight and list of available cataloged date datasets, this generator will first query usage & metadata about
 * used display forms and attributes. With this, the function will lookup date datasets for each display form
 * used in insight's attribute buckets and attribute filters.
 *
 * Note: that cataloged date datasets already contain mapping of dataset → attribute → default display form. However
 * this cannot be used because the code cannot expect that the insights only use date dataset's default display forms.
 *
 * @param insight - insight work with
 * @param attributeToDataset - lookup table of date attributes to datasets
 */
function lookupDatasetsUsedInAttributesAndFilters(insight, attributeToDataset) {
    var insightAttributes, _a, inAttributes, inFilters, displayForms, capabilities, displayFormsMap, datasetLookup;
    return tslib_1.__generator(this, function (_b) {
        switch (_b.label) {
            case 0: return [4 /*yield*/, effects_1.call(queryCall_1.query, queries_1.queryInsightAttributesMeta(insight))];
            case 1:
                insightAttributes = _b.sent();
                _a = insightAttributes.usage, inAttributes = _a.inAttributes, inFilters = _a.inFilters, displayForms = insightAttributes.displayForms;
                return [4 /*yield*/, effects_1.select(backendCapabilitiesSelectors_1.selectBackendCapabilities)];
            case 2:
                capabilities = _b.sent();
                displayFormsMap = objRefMap_1.newDisplayFormMap(displayForms, capabilities.hasTypeScopedIdentifiers);
                datasetLookup = function (ref) {
                    var _a;
                    var displayForm = displayFormsMap.get(ref);
                    // if this bombs then the query insight attributes is messed up as it does not include display forms meta
                    // for some display form used in the insight
                    ts_invariant_1.invariant(displayForm);
                    var attributeRef = displayForm.attribute;
                    return (_a = attributeToDataset.get(attributeRef)) === null || _a === void 0 ? void 0 : _a.dataset;
                };
                return [2 /*return*/, {
                        usedInAttributes: compact_1.default(inAttributes.map(datasetLookup)),
                        usedInAttributeFilters: compact_1.default(inFilters.map(datasetLookup)),
                    }];
        }
    });
}
function lookupDatasetsInInsight(insight) {
    var catalogDateDatasets, catalogDateAttributeToDatasets, usedInDateFilters, usedInAttributesAndFilters, usedInAttributes, usedInAttributeFilters;
    return tslib_1.__generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, effects_1.select(catalogSelectors_1.selectAllCatalogDateDatasetsMap)];
            case 1:
                catalogDateDatasets = _a.sent();
                return [4 /*yield*/, effects_1.select(catalogSelectors_1.selectCatalogDateAttributeToDataset)];
            case 2:
                catalogDateAttributeToDatasets = _a.sent();
                usedInDateFilters = lookupDatasetsUsedInDateFilters(insight, catalogDateDatasets);
                return [4 /*yield*/, effects_1.call(lookupDatasetsUsedInAttributesAndFilters, insight, catalogDateAttributeToDatasets)];
            case 3:
                usedInAttributesAndFilters = _a.sent();
                usedInAttributes = usedInAttributesAndFilters.usedInAttributes, usedInAttributeFilters = usedInAttributesAndFilters.usedInAttributeFilters;
                return [2 /*return*/, {
                        usedInDateFilters: usedInDateFilters,
                        usedInAttributes: usedInAttributes,
                        usedInAttributeFilters: usedInAttributeFilters,
                    }];
        }
    });
}
function queryService(ctx, query) {
    var insightOrRef, correlationId, insight, dateDatasets, usedInInsight, usedInDateFilters, usedInAttributes, usedInAttributeFilters, mostImportantFromInsight, allAvailableDateDatasets, dateDatasetDisplayNames;
    var _a, _b;
    return tslib_1.__generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                insightOrRef = query.payload.insightOrRef, correlationId = query.correlationId;
                if (!sdk_model_1.isObjRef(insightOrRef)) return [3 /*break*/, 2];
                return [4 /*yield*/, effects_1.select(insightsSelectors_1.selectInsightByRef(insightOrRef))];
            case 1:
                insight = _c.sent();
                if (!insight) {
                    throw general_1.invalidQueryArguments(ctx, "Insight with ref " + sdk_model_1.objRefToString(insightOrRef) + " does not exist on the dashboard", correlationId);
                }
                return [3 /*break*/, 3];
            case 2:
                insight = insightOrRef;
                _c.label = 3;
            case 3: return [4 /*yield*/, effects_1.call(loadAvailableDateDatasets_1.loadDateDatasetsForInsight, ctx, insight)];
            case 4:
                dateDatasets = _c.sent();
                return [4 /*yield*/, effects_1.call(lookupDatasetsInInsight, insight)];
            case 5:
                usedInInsight = _c.sent();
                usedInDateFilters = usedInInsight.usedInDateFilters, usedInAttributes = usedInInsight.usedInAttributes, usedInAttributeFilters = usedInInsight.usedInAttributeFilters;
                mostImportantFromInsight = (_b = (_a = usedInDateFilters[0]) !== null && _a !== void 0 ? _a : usedInAttributes[0]) !== null && _b !== void 0 ? _b : usedInAttributeFilters[0];
                allAvailableDateDatasets = uniqBy_1.default(tslib_1.__spread(dateDatasets, usedInDateFilters, usedInAttributes, usedInAttributeFilters).filter(sdk_model_1.isCatalogDateDataset), function (d) { return sdk_model_1.serializeObjRef(d.dataSet.ref); });
                dateDatasetDisplayNames = fromPairs_1.default(allAvailableDateDatasets.map(function (d) { return [d.dataSet.title, dateDatasetOrdering_1.sanitizeDateDatasetTitle(d)]; }));
                return [2 /*return*/, {
                        dateDatasets: dateDatasets,
                        dateDatasetsOrdered: dateDatasetOrdering_1.sortByRelevanceAndTitle(dateDatasets, dateDatasetDisplayNames),
                        usedInDateFilters: usedInDateFilters,
                        usedInAttributes: usedInAttributes,
                        usedInAttributeFilters: usedInAttributeFilters,
                        dateDatasetDisplayNames: dateDatasetDisplayNames,
                        mostImportantFromInsight: mostImportantFromInsight,
                        allAvailableDateDatasets: allAvailableDateDatasets,
                    }];
        }
    });
}
//# sourceMappingURL=queryInsightDateDatasets.js.map